/**
 * @fileOverview The clock: a step's reading time running out, and the controls over it — play, pause,
 * speed, and the hold while the reader's mouse is on the text.
 */

import { SPEEDS } from "./timing"
import { elapsed, hold, isActive, isBusy, isFrameLoading, landed, playing, speed, stepIndex, worked } from "./state"
import { dwellMs, isFinished, isLast, playable } from "./tour"
import { moveFrom, seek, showEnd } from "./moves"

let rafHandle = 0
let lastTick = 0

/**
 * One frame of playback.
 *
 * `dt` is capped, so a tab that was in the background for a minute does not come back and skip the
 * step it was on; `requestAnimationFrame` stops in a hidden tab anyway, so the walkthrough simply
 * waits there.
 */
function tick(now: number) {
  if (!isActive.value) {
    rafHandle = 0
    return
  }

  const dt = lastTick ? Math.min(now - lastTick, 100) : 0
  lastTick = now

  // The demonstration is part of the step, so its time is counted the same way: from the move
  // starting until the step lands. Not `isBusy`, which is true for that whole stretch — that is the
  // demonstration. Only a frame still booting is left out, since there is nothing to watch yet.
  if (playing.value && !landed.value && !hold.value && !isFrameLoading.value) worked.value += dt * speed.value

  if (playing.value && landed.value && !hold.value && !isBusy.value) {
    elapsed.value = Math.min(elapsed.value + dt * speed.value, dwellMs.value)
    if (elapsed.value >= dwellMs.value) {
      // The last step waits for the reader rather than closing under them — on the end screen.
      if (isLast.value) showEnd()
      else void moveFrom(stepIndex.value + 1, 1, "auto")
    }
  }

  rafHandle = requestAnimationFrame(tick)
}

export function startClock() {
  if (rafHandle) return
  lastTick = 0
  rafHandle = requestAnimationFrame(tick)
}

export function stopClock() {
  if (rafHandle) cancelAnimationFrame(rafHandle)
  rafHandle = 0
}

export function togglePlay() {
  if (isFinished.value) {
    // From the top: the reader has seen the end and asked to see it again.
    playing.value = true
    seek(playable.value[0] ?? 0)
    return
  }
  playing.value = !playing.value
}

/** Stops or resumes the clock without `togglePlay`'s "from the top" — for the shortcut list. */
export function setPlaying(value: boolean) {
  playing.value = value
}

/** Chosen from the speed menu. Anything not on the list is ignored rather than coerced. */
export function setSpeed(value: number) {
  if ((SPEEDS as readonly number[]).includes(value)) speed.value = value
}

/** One speed slower or faster, as the `<` and `>` keys ask. Stops at either end rather than wrapping. */
export function stepSpeed(direction: 1 | -1): number {
  const at = (SPEEDS as readonly number[]).indexOf(speed.value)
  setSpeed(SPEEDS[Math.min(SPEEDS.length - 1, Math.max(0, at + direction))])
  return speed.value
}

/** Set while the reader's mouse is on the dot or the text. */
export function setHold(value: boolean) {
  hold.value = value
}
